import { useEffect, useRef } from 'react'
import { useReducedMotion, useSpring } from 'framer-motion'
import { useIsDark } from '@highlight/ui'

// Dots sit on a lattice anchored at the field's center, and each dot's radius
// follows the radial falloff sampled at its cell center — so the light
// dissolves into shrinking dots rather than a uniformly masked grid.
const CELL = 7 // CSS px — the curating bloom's tightest cell
const GATHER_CELL = 16 // the curating scene's opening cell — dots start scattered-coarse
const FIELD_EXPONENT = 1.4

// Success green glows hotter against a dark background at equal alpha, so
// dark runs quieter (mirrors the curating halftone's peak-alpha split)
// Defaults are tuned for --success; a lighter hue wants its own pair.
const LIGHT_PEAK_ALPHA = 0.1
const DARK_PEAK_ALPHA = 0.05

// Same settle feel as the curating scene's stage transitions
const POSE_SPRING = { stiffness: 45, damping: 18 }

// At rest, dots reach the bottom corners but fade before the top edge.
const RADIUS_X_SHARE = 1.25 // of the card's width
const RADIUS_Y_SHARE = 1.0 // of the card's height

// Confirmation surge: the bloom first overshoots far enough that dots wash
// the entire card — the unmissable "it worked" beat — then recedes to the
// resting corona. The hold gives the surge spring time to mostly settle
// before the recede starts.
const SURGE_BLOOM = 1.9
const REST_BLOOM = 1
const SURGE_INTENSITY = 1.6 // dots run hotter during the surge, settle to 1
const REST_INTENSITY = 1
const SURGE_HOLD_MS = 1100

// Gathering ripple, straight from the curating shader: a wave of dot-swell
// traveling toward the source while the pose springs run, easing away once
// they settle. Wavelength is scaled down from the fullscreen scene to the
// card so a couple of crests stay visible in the corona.
const RIPPLE_AMPLITUDE = 0.35
const RIPPLE_WAVELENGTH = 64 // CSS px crest-to-crest
const RIPPLE_EASE = 0.04 // per-frame exponential approach to the target
const WAVE_SPEED = 2.86 // phase speed in radians per second, crests move inward
const RIPPLE_REST = 0.004 // amplitude below which the draw loop parks

/** The shader's field(): smoothstep falloff from the source, eased hot. */
function fieldAt(normalizedDistance: number): number {
  const d = Math.min(normalizedDistance, 1)
  const eased = d * d * (3 - 2 * d)
  return Math.pow(1 - eased, FIELD_EXPONENT)
}

/** The bloom covers the card once, then settles into a halftone corona. Remount with a fresh key to replay. */
interface SuccessHalftoneProps {
  /**
   * When false, paint the resting corona without the surge play-through —
   * for cards that were already connected when the screen appeared, where
   * a confirmation beat would be announcing nothing.
   */
  animate?: boolean
  /** Position of the field's brightest point within its container. */
  origin?: 'bottom' | 'bottom-right' | 'top-right'
  /** Theme colour variable the dots take, in any CSS colour format. */
  colorVariable?: string
  /**
   * Peak dot alpha at the field's centre. Scalars rather than a pair object so
   * the draw effect's dependencies stay stable across renders.
   */
  peakAlphaLight?: number
  peakAlphaDark?: number
}

export function SuccessHalftone({
  animate = true,
  origin = 'bottom',
  colorVariable = '--success',
  peakAlphaLight = LIGHT_PEAK_ALPHA,
  peakAlphaDark = DARK_PEAK_ALPHA,
}: SuccessHalftoneProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const isDark = useIsDark()
  const reducedMotion = useReducedMotion() ?? false
  // Bloom progress scaling both ellipse radii together: 0 → surge → rest
  const bloom = useSpring(0, POSE_SPRING)
  // Lattice pitch, tightening as the field settles — the curating gather
  const cell = useSpring(GATHER_CELL, POSE_SPRING)
  // Brightness multiplier on the dots' alpha, cooling as the surge recedes
  const intensity = useSpring(SURGE_INTENSITY, POSE_SPRING)
  // Set once the play-through settles, so an effect re-run (theme flip)
  // repaints the resting pose instead of replaying the surge
  const playedRef = useRef(false)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) {
      return
    }
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      return
    }

    const color = getComputedStyle(document.documentElement).getPropertyValue(colorVariable).trim()
    const peakAlpha = isDark ? peakAlphaDark : peakAlphaLight

    let ripple = 0

    const render = () => {
      const dpr = window.devicePixelRatio || 1
      const width = Math.round(canvas.clientWidth * dpr)
      const height = Math.round(canvas.clientHeight * dpr)
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width
        canvas.height = height
      }
      ctx.clearRect(0, 0, width, height)
      ctx.fillStyle = color

      const progress = bloom.get()
      const cornerRadius = Math.min(width, height) * 1.25 * progress
      const radiusX = origin === 'top-right' ? cornerRadius : RADIUS_X_SHARE * width * progress
      const radiusY = origin === 'top-right' ? cornerRadius : RADIUS_Y_SHARE * height * progress
      if (radiusX <= 0 || radiusY <= 0) {
        return
      }

      const cellSize = Math.max(cell.get(), 2) * dpr
      const waveK = (2 * Math.PI) / (RIPPLE_WAVELENGTH * dpr)
      const phase = (performance.now() / 1000) * WAVE_SPEED
      const glow = intensity.get() * peakAlpha
      const cornerInset = 18 * dpr
      // `bottom-right` stays pinned to the bottom edge: the field reads as
      // light spilling up from under the card, where insetting it upward would
      // make it a shape sitting on the card instead.
      const centerX = origin === 'bottom' ? width / 2 : width - cornerInset
      const centerY = origin === 'top-right' ? cornerInset : height

      for (let column = Math.floor(-centerX / cellSize); centerX + column * cellSize < width; column++) {
        for (let row = Math.floor(-centerY / cellSize); centerY + row * cellSize < height; row++) {
          const x = centerX + (column + 0.5) * cellSize
          const y = centerY + (row + 0.5) * cellSize
          const field = fieldAt(Math.hypot((x - centerX) / radiusX, (y - centerY) / radiusY))
          // Phase k·d + t moves crests toward the source, swelling dots as
          // each wave passes through the corona (the shader's `wave`)
          const wave = 1 + ripple * Math.sin(Math.hypot(x - centerX, y - centerY) * waveK + phase)
          const rippledField = Math.min(field * wave, 1)
          const dotRadius = cellSize * 0.5 * rippledField
          if (dotRadius < 0.5) {
            continue
          }
          // Like the shader, alpha rides the field so dots fade as they shrink
          ctx.globalAlpha = Math.min(rippledField * glow, 1)
          ctx.beginPath()
          ctx.arc(x, y, dotRadius, 0, 2 * Math.PI)
          ctx.fill()
        }
      }
    }

    if (reducedMotion || !animate || playedRef.current) {
      bloom.jump(REST_BLOOM)
      cell.jump(CELL)
      intensity.jump(REST_INTENSITY)
      render()
      return
    }

    // Start from the pristine pre-play pose — an earlier effect run (an
    // `animate` flip, a theme change mid-play) may have left the springs
    // elsewhere, and the choreography only reads right from here.
    bloom.jump(0)
    cell.jump(GATHER_CELL)
    intensity.jump(SURGE_INTENSITY)

    // Stage 1: gather into the overshot, card-covering surge…
    bloom.set(SURGE_BLOOM)
    cell.set(CELL)
    let receding = false
    // …stage 2: recede to the resting corona.
    const recedeTimer = setTimeout(() => {
      receding = true
      bloom.set(REST_BLOOM)
      intensity.set(REST_INTENSITY)
    }, SURGE_HOLD_MS)

    let frame = 0
    const tick = () => {
      const settled =
        receding &&
        Math.abs(bloom.get() - REST_BLOOM) < 0.001 &&
        Math.abs(cell.get() - CELL) < 0.05 &&
        Math.abs(intensity.get() - REST_INTENSITY) < 0.005
      ripple += ((settled ? 0 : RIPPLE_AMPLITUDE) - ripple) * RIPPLE_EASE
      render()
      if (settled && ripple < RIPPLE_REST) {
        // Park on the exact settled pose so the last frame carries no wave
        ripple = 0
        playedRef.current = true
        render()
        return
      }
      frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => {
      clearTimeout(recedeTimer)
      cancelAnimationFrame(frame)
    }
  }, [bloom, cell, intensity, reducedMotion, animate, isDark, origin, colorVariable, peakAlphaLight, peakAlphaDark])

  return <canvas ref={canvasRef} aria-hidden className="pointer-events-none absolute inset-0 h-full w-full" />
}
